/* eslint relay/unused-fields: 0 */
import { useEffect } from 'react';
import { graphql, useFragment } from 'react-relay';
import serverVars from 'server-vars';
import { datadog } from 'dibs-datadog/exports/datadog.ts';
import { trackEvent, eventNameConstants, type TrackingObject } from 'dibs-tracking';
import { trackAbTestBucket } from 'dibs-tracking/exports/prometheus.ts';
import { getConsentedBuyerId, getAbOptIn, getSellerId } from 'dibs-cookie-jar';
import { getAbUuid } from 'dibs-cookie-jar/exports/abTestHelpers.ts';
import { trackAbTestBucketEvent, type AbTestBucketDecisions } from 'dibs-tracking';
import {
    bucketABTest,
    isABTestConfig,
    audienceMatchesDeviceType,
    AB_TEST_TYPES,
} from 'dibs-bucket-ab-tests';
import { type AbTestV2 } from './types.ts';
import { OPT_IN_CONFIG, type OptInConfig } from './constants.ts';
import {
    type clientAbTestV2_useAbTestV2BuyerUser_user$data,
    type clientAbTestV2_useAbTestV2BuyerUser_user$key,
} from './__generated__/clientAbTestV2_useAbTestV2BuyerUser_user.graphql.ts';

type AbTestEventOptions = Parameters<typeof trackEvent>[2];

type AbTestType = keyof typeof AB_TEST_TYPES;

/**
 * * overrideUserId --  Pass a user id to use as the seedbase for a BUYER_USER test. If not passed, will
 * use the currently logged in buyer.
 * * testTypes -- Pass an array of test types to bucket. Used mainly to track bucketing decsions.
 * Defaults to [BUYER_CLIENT, BUYER_USER]
 */
type GetAbTestV2Options = {
    overrideUserId?: string;
    testTypes?: AbTestType[];
};

type BuyerCookieOrClientAbTestType =
    | typeof AB_TEST_TYPES.BUYER_COOKIE
    | typeof AB_TEST_TYPES.BUYER_CLIENT;
type BuyerAbTestType = BuyerCookieOrClientAbTestType | typeof AB_TEST_TYPES.BUYER_USER;

const isBuyerCookieOrClientTest = (
    testType: AbTestType
): testType is BuyerCookieOrClientAbTestType =>
    testType === AB_TEST_TYPES.BUYER_COOKIE || testType === AB_TEST_TYPES.BUYER_CLIENT;

const isBuyerTest = (testType: AbTestType): testType is BuyerAbTestType =>
    isBuyerCookieOrClientTest(testType) || testType === AB_TEST_TYPES.BUYER_USER;

const getRampedVariant = (config: AbTestV2['config']): string | null =>
    (isBuyerTest(config.testType) &&
        config.ramp === 100 &&
        config.variations.find(variation => variation.weight === 100)?.value) ||
    null;

function getSeedBase(
    testType: AbTestV2['config']['testType'],
    options?: GetAbTestV2Options
): string | null {
    if (testType === AB_TEST_TYPES.BUYER_USER) {
        if (options?.overrideUserId) {
            return options.overrideUserId;
        }
        return getConsentedBuyerId(document.cookie) || null;
    } else if (testType === AB_TEST_TYPES.SELLER) {
        return getSellerId(document.cookie);
    } else if (isBuyerCookieOrClientTest(testType)) {
        return getAbUuid(document.cookie);
    }
    return null;
}

/**
 * This is the core bucketing function for the client side A/B testing.
 * Do not change this logic without considering the implications for the
 * the CDN-based bucketing (in dibs-fastly-js repo).
 *
 * Exported for testing, do not use directly. Use getAbTestV2 instead.
 */
export function __getClientABTestV2(
    config: AbTestV2['config'],
    options?: GetAbTestV2Options
): AbTestV2 | null {
    if (!config.isActive) {
        return null;
    }

    const rampedVariant = getRampedVariant(config);
    // When a BUYER_COOKIE, BUYER_CLIENT, or BUYER_USER test is fully rolled out to one variant,
    // skip bucketing and return that variant without requiring an ab_uuid or consentedUserId cookie.
    if (rampedVariant) {
        return { variant: rampedVariant, config };
    }
    const seedBase = getSeedBase(config.testType, options);
    if (seedBase) {
        const variant = bucketABTest(config, seedBase);
        if (variant) {
            return { variant, config };
        }
    }
    return null;
}

/**
 * Get the A/B test configuration for the given test name.
 * If the user has an opt-in cookie for the test, it will be used to determine the variant.
 * @param testName - The name of the test to get the bucketed variant for.
 * @param options - (optional) Options for the test.
 * * overrideUserId? --  Pass a user id to use as the seedbase for a BUYER_USER test.
 * If not passed, will use the currently logged in buyer
 * * testTypes? -- Pass an array of test types to bucket. You can use this to force bucketing on the client,
 * even if the test type is bucketed on the server. Defaults to [BUYER_CLIENT, BUYER_USER]
 * @returns The A/B test configuration {AbTestV2} or null
 */
export function getAbTestV2(
    testName: string,
    options?: GetAbTestV2Options
): AbTestV2 | OptInConfig | null {
    if (typeof window === 'undefined') {
        throw new Error('getAbTestV2 should only be called on the client!');
    }
    // get the { variant?, config } object for the test
    const abTest = serverVars.get('abTestsV2.' + testName) as
        | AbTestV2
        | OptInConfig
        | null
        | undefined;

    if (!(abTest && (abTest.config === OPT_IN_CONFIG || isABTestConfig(abTest.config)))) {
        // maybe we don't have this test
        return null;
    }
    // These are the test types that we want to bucket on the client
    // We allow the consumer to override this list with the testTypes option
    // because we send test buckets to the BE for BUYER_COOKIE tests
    // even if they weren't bucketed on the CDN because of the routing config
    const testTypesToBucket: string[] = options?.testTypes || [
        AB_TEST_TYPES.BUYER_USER,
        AB_TEST_TYPES.BUYER_CLIENT,
    ];

    // These are the test types that can be bucketed on the server
    const testTypesWithServerDecision: string[] = [
        AB_TEST_TYPES.BUYER_COOKIE,
        AB_TEST_TYPES.SELLER,
        AB_TEST_TYPES.SELLER_PREBUCKET,
        AB_TEST_TYPES.APP_SELLER_PREBUCKET,
    ];

    const deviceType = serverVars.get('settings.deviceType') as unknown;
    if (abTest.config === OPT_IN_CONFIG) {
        return abTest;
    } else if (abTest.variant && testTypesWithServerDecision.includes(abTest.config.testType)) {
        // abTest.variant may be set for the following use-cases:
        // - bucket determined in the CDN for BUYER_COOKIE tests
        // - bucket determined in GraphQL via DAL for server-side SELLER tests
        return abTest;
    } else if (
        testTypesToBucket.includes(abTest?.config?.testType || '') &&
        audienceMatchesDeviceType(abTest.config.audience, deviceType)
    ) {
        let abOptInParams = new URLSearchParams();
        try {
            const abOptIn = getAbOptIn(document.cookie) || '';
            abOptInParams = new URLSearchParams(abOptIn);
        } catch {
            // fail silently...
        }

        const abOptInVariant = abOptInParams.get(abTest.config.testName);

        if (abOptInVariant) {
            return {
                ...abTest,
                variant: abOptInVariant,
            };
        }
        // bucket the test on the client
        return __getClientABTestV2(abTest.config, options);
    }
    // Not a test we're currently interested in bucketing
    return null;
}

function trackAbTestEvent({
    variant,
    testName,
    ramp,
    options,
    trackingProperties,
    abTestType,
}: {
    variant: string | undefined;
    testName: string | undefined;
    ramp: number | undefined;
    options: AbTestEventOptions;
    trackingProperties?: TrackingObject;
    abTestType?: string;
}): void {
    trackEvent(
        {
            category: 'a/b testing v3',
            eventName: eventNameConstants.EVENT_AB_TESTING,
            action: testName,
            interaction_type: testName,
            label: variant,
            step_interaction_name: variant,
            value: ramp,
            ab_uuidCookie: getAbUuid(document.cookie),
            ...trackingProperties,
        },
        null,
        options
    );
    if (serverVars.get('NODE_DD_TRACK_AB_TESTS')) {
        datadog.run.addAction(eventNameConstants.EVENT_AB_TESTING, {
            testName,
            variant,
        });
    }
    trackAbTestBucket({ testName, variant, ramp, abTestType });
}

/**
 * Some tests don't use the default bucketing logic, so we can't use the trackAbTestV2Variant function.
 */
export function trackNonStandardAbTest(options: { variant: string; testName: string }): void {
    const { variant, testName } = options;
    trackAbTestEvent({
        variant,
        testName,
        ramp: undefined,
        options: undefined,
    });
}

export function trackAbTestV2Variant(
    testName: string,
    options?: AbTestEventOptions,
    abTestOptions?: GetAbTestV2Options,
    trackingProperties?: TrackingObject
): void {
    const abTest = getAbTestV2(testName, abTestOptions);
    const ramp = abTest?.config === OPT_IN_CONFIG ? undefined : abTest?.config.ramp;
    const abTestType = abTest?.config === OPT_IN_CONFIG ? undefined : abTest?.config.testType;
    if (abTest) {
        trackAbTestEvent({
            variant: abTest.variant,
            testName,
            ramp,
            options,
            trackingProperties,
            abTestType,
        });
    }
}

type AbTestV2Type =
    | NonNullable<clientAbTestV2_useAbTestV2BuyerUser_user$data['abTestV2List']>[number]
    | null;

const trackedTests = new Set<string>();

export function useAbTestV2BuyerUser({
    testName,
    user: userRef,
    eventOptions,
    shouldFireTracking = true,
}: {
    testName: string;
    user: clientAbTestV2_useAbTestV2BuyerUser_user$key | null | undefined;
    eventOptions?: AbTestEventOptions;
    shouldFireTracking?: boolean;
}): AbTestV2Type {
    const user = useFragment(
        graphql`
            fragment clientAbTestV2_useAbTestV2BuyerUser_user on User {
                abTestV2List(testType: BUYER_USER) {
                    config {
                        createdDate
                        modifiedDate
                        testName
                        description
                        testType
                        audience
                        ramp
                        isActive
                        route
                        variations {
                            value
                            weight
                        }
                    }
                    variant
                }
            }
        `,
        userRef
    );

    const abTestV2 = user?.abTestV2List?.find(test => test?.config?.testName === testName) || null;

    const variant = abTestV2?.variant ?? undefined;
    const ramp = abTestV2?.config?.ramp ?? undefined;
    const immediate = eventOptions?.immediate;

    useEffect(() => {
        // check trackedTests to ensure that tracking is called only once
        // even if hook is mounted multiple times
        if (variant && !trackedTests.has(testName) && shouldFireTracking) {
            trackAbTestEvent({
                variant,
                testName,
                ramp,
                options: immediate !== undefined ? { immediate } : undefined,
                abTestType: AB_TEST_TYPES.BUYER_USER,
            });
            trackedTests.add(testName);
        }
    }, [variant, testName, ramp, immediate, shouldFireTracking]);

    return abTestV2;
}

/**
 * Track the results of active A/B tests for the given test types.
 * This should be called in the initialization code in dbl and dal.
 */
export function bucketActiveABTests(testTypes: AbTestType[]): void {
    // Get the abTestsV2 object from server vars
    const abTestsV2 = serverVars.get('abTestsV2') as Record<string, AbTestV2> | null;
    const abTestDecisions: AbTestBucketDecisions[] = [];
    if (abTestsV2) {
        for (const [testName, test] of Object.entries(abTestsV2)) {
            if (test && isABTestConfig(test.config)) {
                const result = getAbTestV2(testName, { testTypes });
                if (result?.variant) {
                    abTestDecisions.push({
                        testName,
                        testVariant: result.variant,
                    });
                }
            }
        }
    }
    if (abTestDecisions.length > 0) {
        trackAbTestBucketEvent(abTestDecisions);
    }
}

export const setDatadogABTestValues = (): void => {
    const abTests = serverVars.get('abTestsV2');
    for (const testName of Object.keys(abTests)) {
        const abTest = getAbTestV2(testName);
        if (abTest?.variant) {
            datadog.run.setGlobalContextProperty(testName, abTest.variant);
        }
    }
};
